import React, {useEffect, useState} from 'react';
import UpdateScorerForm from 'app/components/match/liveResult/updateScorer/UpdateScorerForm';
import {
  Inputs,
  MatchEventGoalResource,
  MatchPlayersResponse,
  StoreGoalEventData,
} from 'app/components/match/liveResult/updateScorer/sharedTypes';
import {catchDataError} from 'app/helpers/apiHelpers';
import {AxiosResponse} from 'axios';
import {api, authorizedAPI} from 'app/services/api';
import {UseFormSetError} from 'react-hook-form';
import {useSoftTickerContext} from 'app/hooks/useSoftTickerContext';
import {sendEventToGA4} from 'app/helpers/gtagHelpers';
import isAxiosError from 'app/helpers/isAxiosError';
import {ApiErrorResponseWithFields} from 'app/common/errorTypes';

const getPlayers = (matchSlug: string): Promise<AxiosResponse<MatchPlayersResponse>> => {
  return api.get(`/matches/${matchSlug}/players`);
};

interface FieldMapper {
  [name: string]: keyof Inputs;
}

const fieldMapper: FieldMapper = {
  assistantId: 'assistant',
  awayGoals: 'awayGoals',
  homeGoals: 'homeGoals',
  minute: 'minute',
  scorerId: 'scorer',
  teamId: 'team',
  type: 'category',
};

const storeGoalEvent = (
  matchSlug: string,
  data: StoreGoalEventData,
  matchEventId: number | null
): Promise<AxiosResponse<MatchEventGoalResource>> => {
  return matchEventId
    ? authorizedAPI.put(`/matches/${matchSlug}/goals/${matchEventId}`, data)
    : authorizedAPI.post(`/matches/${matchSlug}/goals`, data);
};

interface UpdateScorerProps {
  match: any;
  onClose: () => void;
  onUpdateScorer: (matchEvent: MatchEventGoalResource, isUpdate: boolean) => void;
  onDelete?: (matchEventId: number) => void;
}

const UpdateScorer = ({match, onClose, onUpdateScorer, onDelete}: UpdateScorerProps) => {
  const [players, setPlayers] = useState<MatchPlayersResponse | null>(null);
  const [loadingPlayers, setLoadingPlayers] = useState<boolean>(false);
  const [playersError, setPlayersError] = useState<{status: number; message: string} | null>(null);
  const [submissionLoading, setSubmissionLoading] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const {softTickerData} = useSoftTickerContext();

  useEffect(() => {
    if (match.slug) {
      setLoadingPlayers(true);
      getPlayers(match.slug)
        .then(response => {
          setPlayers(response.data);
          setLoadingPlayers(false);
        })
        .catch(error => {
          setPlayersError(catchDataError(error));
          setLoadingPlayers(false);
        });
    }
  }, [match]);

  const handleUpdateScorer = (matchEvent: MatchEventGoalResource, isUpdate: boolean) => {
    if (!isUpdate) {
      sendEventToGA4('soft_ticker_report_scorer', {
        soft_ticker_action: 'save',
        ui_view: softTickerData.initiatorView ?? 'layer_report_scorer',
        match_slug: match.slug,
      });
    }
    onUpdateScorer(matchEvent, isUpdate);
  };

  const onSubmit = (data: StoreGoalEventData, setFieldError: UseFormSetError<Inputs>) => {
    setSubmissionLoading(true);
    storeGoalEvent(match.slug, data, softTickerData.matchEventId)
      .then(response => {
        setSubmissionLoading(false);
        handleUpdateScorer(response.data, !!softTickerData.matchEventId);
      })
      .catch(error => {
        setSubmissionLoading(false);
        if (isAxiosError<ApiErrorResponseWithFields>(error)) {
          const errorData = error.response?.data?.error;
          if (errorData?.code === 422) {
            // Validation-Error
            const fields = errorData.fields;
            if (fields && Object.keys(fields).length) {
              Object.keys(fields).forEach(field => {
                const input = fieldMapper[field];
                setFieldError(input, {
                  type: 'custom',
                  message: fields[field]?.[0] ?? errorData.translated_message,
                });
              });
              return;
            }
          }
          if (errorData?.translated_message) {
            return setRequestError(errorData.translated_message);
          }
        }
        return setRequestError('Es ist ein unbekannter Fehler aufgetreten, versuchen sie es später erneut.');
      });
  };

  return (
    <UpdateScorerForm
      match={match}
      onClose={onClose}
      onDelete={onDelete}
      initData={softTickerData.scorerFormData ?? softTickerData.resultData}
      matchEventId={softTickerData.matchEventId}
      players={players}
      loadingPlayers={loadingPlayers}
      onSubmit={onSubmit}
      submissionLoading={submissionLoading}
      submissionError={requestError}
    />
  );
};

export default UpdateScorer;
